// Reports data file - can be easily updated with new reports
export interface ReportAuthor {
  id: string;
  name: string;
  title: string;
  bio: string;
  image: string;
  linkedinUrl: string;
  role: string;
}

export interface ReportSection {
  id: string;
  heading: string;
  content: string;
}

export interface Report {
  id: string;
  title: string;
  image: string;
  date: string;
  category: 'survey' | 'research' | 'ebooks';
  ctaText: string;
  ctaLink: string;
  featured?: boolean;
  tags?: string[];
  keywords?: string[];
  // Fields for single report page
  sections: ReportSection[];
  authors: ReportAuthor[];
  longDescription?: string;
  downloadUrl?: string;
  summary?: string;
  pdfUrl?: string;
}

export const reports: Report[] = [
  {
    id: 'cost-recruitment-offerings-sep-2025',
    title: 'Cost of Recruitment Offerings - September 2025',
    longDescription: 'Understand how the prices of resume databases, LinkedIn and recruitment agencies is changing for other companies',
    summary: 'Understand how the prices of resume databases, LinkedIn and recruitment agencies is changing for other companies',
    image: '/cost-of-survey.png',
    date: '2025-09-19',
    category: 'survey',
    ctaText: 'View Report',
    ctaLink: '/reports/cost-recruitment-offerings-sep-2025',
    featured: true,
    tags: ['Recruitment', 'Pricing', 'Cost Analysis', '2025'],
    keywords: ['recruitment costs', 'hiring costs analysis', 'job portal pricing', 'LinkedIn recruitment costs', 'recruitment agency pricing', 'talent acquisition costs', 'hiring budget planning', 'recruitment ROI', 'hiring cost optimization', 'recruitment pricing trends'],
    downloadUrl: 'https://drive.google.com/file/d/1i0LZu-txQ_qMl3uaeUFDCvfQhSq76qZg/view?usp=sharing',
    pdfUrl: 'https://drive.google.com/file/d/1i0LZu-txQ_qMl3uaeUFDCvfQhSq76qZg/view?usp=sharing',
    sections: [
      {
        id: 'overview',
        heading: 'Overview',
        content: 'This comprehensive report analyzes the changing landscape of recruitment service pricing across resume databases, LinkedIn, and recruitment agencies. Understanding these cost dynamics is crucial for companies looking to optimize their recruitment spend.'
      },
      {
        id: 'key-insights',
        heading: 'Key Insights',
        content: 'The report reveals significant shifts in pricing models across different recruitment channels, with implications for budget planning and vendor selection strategies.'
      }
    ],
    authors: [
      {
        id: 'cutshort-research',
        name: 'Cutshort Research Team',
        title: 'Research & Analytics',
        bio: 'Our research team analyzes recruitment trends, pricing models, and industry dynamics to provide actionable insights for hiring teams.',
        image: '/placeholder.svg',
        linkedinUrl: 'https://linkedin.com/company/cutshort',
        role: 'Research Team'
      }
    ]
  },
  {
    id: 'how-to-negotiate-salaries-ebook',
    title: 'How to Negotiate Salaries with Candidates: A Win-Win Approach',
    longDescription: 'Making offers is easy. Making offers that get accepted is not. This guide explains the fundamentals for today\'s market.',
    summary: 'Making offers is easy. Making offers that get accepted is not. This guide explains the fundamentals for today\'s market.',
    image: '/making-offers-ebook.png',
    date: '2025-08-12',
    category: 'ebooks',
    ctaText: 'View Ebook',
    ctaLink: '/reports/how-to-negotiate-salaries-ebook',
    featured: true,
    tags: ['Salary Negotiation', 'Hiring', 'Ebook', 'Recruitment'],
    keywords: ['salary negotiation', 'candidate negotiation', 'hiring negotiation', 'offer negotiation', 'compensation negotiation', 'salary discussion', 'hiring best practices', 'recruitment negotiation', 'talent acquisition', 'hiring process'],
    downloadUrl: 'https://drive.google.com/file/d/1gUiBWA3YzW_PZFIvUw9nSWI8XMhFntTI/view?usp=share_link',
    pdfUrl: 'https://drive.google.com/file/d/1gUiBWA3YzW_PZFIvUw9nSWI8XMhFntTI/view?usp=share_link',
    sections: [
      {
        id: 'overview',
        heading: 'Overview',
        content: 'This comprehensive ebook provides practical strategies for negotiating salaries with candidates in today\'s competitive market. Learn how to create win-win scenarios that benefit both the company and the candidate.'
      },
      {
        id: 'key-topics',
        heading: 'What You\'ll Learn',
        content: '1. Understanding market rates and compensation trends\n2. Building trust and transparency in salary discussions\n3. Creative compensation packages beyond base salary\n4. Handling counter-offers and negotiations\n5. Legal considerations and best practices\n6. Creating long-term value for both parties'
      }
    ],
    authors: [
      {
        id: 'cutshort-team',
        name: 'Cutshort Team',
        title: 'Recruitment Experts',
        bio: 'Our team of recruitment experts has helped thousands of companies improve their hiring processes and candidate experience.',
        image: '/placeholder.svg',
        linkedinUrl: 'https://linkedin.com/company/cutshort',
        role: 'Content Team'
      }
    ]
  },
  {
    id: '6-myths-ai-recruiting-ebook',
    title: '6 Myths About AI in Recruiting',
    longDescription: 'Recruiters often believe in some myths that impact their usage of AI. This guide debunks some common misconceptions.',
    summary: 'Recruiters often believe in some myths that impact their usage of AI. This guide debunks some common misconceptions.',
    image: '/6-myths.png',
    date: '2024-12-05',
    category: 'ebooks',
    ctaText: 'View Ebook',
    ctaLink: '/reports/6-myths-ai-recruiting-ebook',
    featured: true,
    tags: ['AI', 'Recruiting', 'Myths', 'Ebook'],
    keywords: ['AI recruiting myths', 'artificial intelligence hiring', 'AI recruitment misconceptions', 'recruitment AI myths', 'hiring AI myths', 'AI in talent acquisition', 'recruitment technology myths', 'AI screening myths', 'automated hiring myths', 'recruitment AI debunked'],
    downloadUrl: 'https://drive.google.com/file/d/1Bw6z6Dv060hd9dUjCypQPjWr0MKdavg-/view?usp=share_link',
    pdfUrl: 'https://drive.google.com/file/d/1Bw6z6Dv060hd9dUjCypQPjWr0MKdavg-/view?usp=share_link',
    sections: [
      {
        id: 'overview',
        heading: 'Overview',
        content: 'This comprehensive ebook addresses the most common misconceptions about AI in recruiting. Learn the truth behind these myths and how to effectively leverage AI tools in your recruitment process.'
      },
      {
        id: 'what-youll-learn',
        heading: 'What You\'ll Learn',
        content: '1. Myth: AI will replace human recruiters\n2. Myth: AI is too expensive for small companies\n3. Myth: AI eliminates bias in hiring\n4. Myth: AI can\'t understand context and nuance\n5. Myth: AI makes recruitment impersonal\n6. Myth: AI is a one-size-fits-all solution\n\nPlus practical tips for implementing AI effectively in your recruitment strategy.'
      }
    ],
    authors: [
      {
        id: 'cutshort-ai-team',
        name: 'Cutshort AI Team',
        title: 'AI & Recruitment Specialists',
        bio: 'Our AI specialists work at the intersection of artificial intelligence and recruitment, helping companies understand and implement AI tools effectively.',
        image: '/placeholder.svg',
        linkedinUrl: 'https://linkedin.com/company/cutshort',
        role: 'AI Specialists'
      }
    ]
  },
  {
    id: 'tech-hiring-trends-2024',
    title: 'Tech Hiring Trends 2024',
    longDescription: 'Explore what changed in the tech hiring world in 2024 and what would lie ahead in the future',
    summary: 'Explore what changed in the tech hiring world in 2024 and what would lie ahead in the future',
    image: '/tech-hiring-2024.png',
    date: '2024-12-15',
    category: 'research',
    ctaText: 'View Research',
    ctaLink: '/reports/tech-hiring-trends-2024',
    featured: true,
    tags: ['Tech Hiring', 'Trends', '2024', 'Research'],
    keywords: ['tech hiring trends 2024', 'technology hiring trends', 'software engineering hiring', 'tech recruitment trends', 'hiring trends 2024', 'technology talent acquisition', 'tech job market trends', 'software developer hiring', 'tech recruitment insights', 'hiring market analysis'],
    downloadUrl: 'https://drive.google.com/file/d/1jZr8SVbpNJxzeE4-kv-TESo4eBBPIVZU/view?usp=share_link',
    pdfUrl: 'https://drive.google.com/file/d/1jZr8SVbpNJxzeE4-kv-TESo4eBBPIVZU/view?usp=share_link',
    sections: [
      {
        id: 'overview',
        heading: 'Overview',
        content: 'This comprehensive research report analyzes the major shifts and trends that shaped the tech hiring landscape in 2024. Based on data from thousands of companies and interviews with industry leaders, this report provides insights into what changed and what to expect in the future.'
      },
      {
        id: 'key-findings',
        heading: 'Key Findings',
        content: '1. Remote work policies and their impact on talent acquisition\n2. Salary trends and compensation evolution\n3. Skills in demand and emerging technologies\n4. Diversity and inclusion initiatives\n5. AI and automation in hiring processes\n6. Market dynamics and economic factors\n7. Future predictions for 2025 and beyond'
      },
      {
        id: 'methodology',
        heading: 'Research Methodology',
        content: 'This report is based on:\n- Survey responses from 500+ tech companies\n- Interviews with 50+ hiring managers and recruiters\n- Analysis of job posting data from major platforms\n- Salary data from compensation surveys\n- Industry reports and market research'
      }
    ],
    authors: [
      {
        id: 'cutshort-research-team',
        name: 'Cutshort Research Team',
        title: 'Research & Analytics',
        bio: 'Our research team analyzes hiring trends, market dynamics, and industry insights to provide data-driven recommendations for tech companies.',
        image: '/placeholder.svg',
        linkedinUrl: 'https://linkedin.com/company/cutshort',
        role: 'Research Team'
      }
    ]
  },
  {
    id: 'definitive-guide-ai-screening-ebook',
    title: 'The Definitive Guide to AI Screening',
    longDescription: 'Seeing high application volumes and keyword stuffing by candidates? AI Screening is becoming a popular option for many companies. But what works and what aspects one should keep in mind?',
    summary: 'Seeing high application volumes and keyword stuffing by candidates? AI Screening is becoming a popular option for many companies. But what works and what aspects one should keep in mind?',
    image: '/ai-screening-ebook.png',
    date: '2024-11-20',
    category: 'ebooks',
    ctaText: 'View Ebook',
    ctaLink: '/reports/definitive-guide-ai-screening-ebook',
    featured: true,
    tags: ['AI Screening', 'Recruitment', 'Ebook', 'Automation'],
    keywords: ['AI screening guide', 'automated candidate screening', 'AI recruitment screening', 'resume screening AI', 'candidate screening automation', 'AI hiring tools', 'recruitment AI screening', 'automated hiring screening', 'AI candidate evaluation', 'screening automation best practices'],
    downloadUrl: 'https://drive.google.com/file/d/1t7amQ8ySdKLiQq95SXALOUs6AUVk4TrD/view?usp=share_link',
    pdfUrl: 'https://drive.google.com/file/d/1t7amQ8ySdKLiQq95SXALOUs6AUVk4TrD/view?usp=share_link',
    sections: [
      {
        id: 'overview',
        heading: 'Overview',
        content: 'This comprehensive guide explores the world of AI screening in recruitment. Learn how to effectively implement AI screening tools to handle high application volumes while maintaining quality and reducing bias in your hiring process.'
      },
      {
        id: 'what-youll-learn',
        heading: 'What You\'ll Learn',
        content: '1. Understanding AI screening technology and its capabilities\n2. Best practices for implementing AI screening tools\n3. How to handle keyword stuffing and application volume challenges\n4. Balancing automation with human oversight\n5. Reducing bias in AI screening processes\n6. Measuring the effectiveness of AI screening\n7. Common pitfalls and how to avoid them\n8. Future trends in AI screening technology'
      },
      {
        id: 'target-audience',
        heading: 'Who This Guide Is For',
        content: 'This guide is designed for:\n- HR professionals and recruiters\n- Talent acquisition teams\n- Hiring managers dealing with high volume applications\n- Companies looking to implement AI in their hiring process\n- Anyone interested in modernizing their recruitment workflow'
      }
    ],
    authors: [
      {
        id: 'cutshort-ai-experts',
        name: 'Cutshort AI Experts',
        title: 'AI & Recruitment Technology Specialists',
        bio: 'Our AI experts specialize in implementing and optimizing AI solutions for recruitment, helping companies improve their hiring efficiency while maintaining quality.',
        image: '/placeholder.svg',
        linkedinUrl: 'https://linkedin.com/company/cutshort',
        role: 'AI Technology Specialists'
      }
    ]
  }
];

// Helper functions for filtering and sorting reports
export const getFeaturedReports = () => reports.filter(report => report.featured);

export const getReportsByCategory = (category: Report['category']) => 
  reports.filter(report => report.category === category);

export const getRecentReports = () => {
  return reports.sort((a, b) => {
    const dateA = new Date(a.date);
    const dateB = new Date(b.date);
    return dateB.getTime() - dateA.getTime();
  });
};

export const getReportById = (id: string) => reports.find(report => report.id === id);

// Category labels for display
export const getCategoryLabel = (category: Report['category']) => {
  switch (category) {
    case 'survey':
      return 'Survey';
    case 'research':
      return 'Research';
    case 'ebooks':
      return 'Ebooks';
    default:
      return category;
  }
};
